import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Utils from '../../../shared/utils';
import ProductLanguage from '../../../shared/product/ProductLanguage';
import { ProductCover } from '../../localcontent/yourtitles/ProductCover';
import ProductSeries from '../../../shared/product/ProductSeries';
import ProductAuthors from '../../../shared/product/ProductAuthors';
import ProductNarrators from '../../../shared/product/ProductNarrators';
import { ProductFormat } from '../../../shared/product/ProductFormat';
import ProductGenres from '../../../shared/product/ProductGenres';
import ProductAge from '../../../shared/product/ProductAge';
import ProductPublisher from '../../../shared/product/ProductPublisher';
import { concreteLoanFormatNameToKey, loanFormatKeyToObject } from '../../../store/loanFormat/actions';
import { PreviewButton } from '../../../shared/preview/PreviewButton';
import { ProductReleaseDate } from '../../../shared/product/ProductReleaseDate';
import { THEME_BLACK, THEME_GREY, themeColor } from '../../../styles/basics/colors';
import { MATERIAL_ICON_EDIT, MATERIAL_ICON_LINK } from '../../../shared/common/Button/Icon';
import { showNotification, TYPE_WARNING } from '../../../store/notifications/actions';
import { Copies } from './Copies';
import StartDate from './StartDate';
import { StCampaignCopiesWrapper, StCampaignDatesWrapper } from './MetaDataStyles';
import EndDate from './EndDate';
import { TotalLoans } from './TotalLoans';
import { TagLine } from './TagLine';
import CampaignUsageBadge from './CampaignUsageBadge';
import { AnalyticsModal } from './AnalyticsModal';
import SettingsModal from './SettingsModal';
import {
    allEndDatesReached,
    autoExtendDateNotReached,
    endDateReached,
    noStartDateAndNoLoansLeft,
    noStartDateSet,
    startDateInFuture,
    startDateReached,
    startDateSetAndNoLoansLeft
} from './ViewStates';
import { AddProduct } from '../../../shared/common/AddProduct/AddProduct';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { LENDING_MODEL_SIM } from '../../../store/constants';
import { daysLeftInCampaignFromNow } from '../../../shared/utils/campaigns/daysLeftInCampaign';
import { log } from '../../../shared/utils/log';
import MetadataUtils from '../../../shared/utils/utils-metadata';
import { MarketingCampaignProductOut, PromotionLinkResponse } from '../../../store/api';
import { useDispatch } from 'react-redux';
import {
    fetchCampaignAnalytics,
    fetchCampaignAnalyticsForPartner
} from '../../../api/marketing/your-campaigns/useCampaignAnalytics';
import { CampaignResult } from '../../../api/marketing/your-campaigns/campaignTypes';
import { usePromotionData } from '../../../api/marketing/your-campaigns/usePromotionData';
import { downloadMarketingMaterial } from '../../../api/marketing/your-campaigns/downloadMarketingMaterial';
import { downloadAnalyticsPdf } from '../../../api/marketing/your-campaigns/downloadAnalyticsPdf';
import { ProductMetaData } from '../../../../models/Product';
import {
    StActionButtons,
    StActions,
    StBadgeGraying,
    StBadges,
    StButtonPos,
    StButtonV2,
    StCampaignDates,
    StCellBottom,
    StCoverGraying,
    StProductListCell,
    StProductUnavailable,
    StTextGraying
} from './campaignListStyled';
import { PromotionModal } from './PromotionModal';
import { useTranslation } from 'react-i18next';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';

interface Props {
    product: MarketingCampaignProductOut & ProductMetaData;
    updateProductCampaignSettings: (
        campaignStart: string,
        campaignEnd: string,
        campaignTagLine: string,
        productId: string
    ) => void;
    loanFormat: string;
    isInPartnerContext: boolean;
    onProductImageClick: () => void;
    onCopyAmountChange: (product: MarketingCampaignProductOut, amount: number) => void;
    onAddPrice: (options: SingleAddOptions) => Promise<void>;
    onSelectProduct: (product: MarketingCampaignProductOut) => void;
    isProductSelected: (product: MarketingCampaignProductOut) => boolean;
}

export const CampaignListItem = (props: Props) => {
    const { t } = useTranslation();
    const { onAddPrice, product } = props;
    const campaignProductId = props.product.id ?? '';
    const dispatch = useDispatch();
    const [showDetails, setShowDetails] = useState(false);
    const [settingsVisible, setSettingsVisible] = useState(false);
    const [promotionVisible, setPromotionVisible] = useState(false);
    const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
    const [productId, setProductId] = useState('');
    const [campaignResults, setCampaignResults] = useState<CampaignResult | null>(null);
    const [campaignResultsForPartner, setCampaignResultsForPartner] = useState<CampaignResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const { data: promotionData, isInitialLoading: isPromotionLoading } = usePromotionData({
        campaignProductId: productId,
        enabled: !!productId && promotionVisible
    });

    useEffect(() => {
        const campaignAnalyticsData = async () => {
            return await fetchCampaignAnalytics(productId);
        };

        const campaignAnalyticsDataForPartner = async () => {
            return await fetchCampaignAnalyticsForPartner(productId);
        };

        if (productId && showAnalyticsModal && !props.isInPartnerContext) {
            setIsLoading(true);
            const data = campaignAnalyticsData();
            data.then(res => {
                setCampaignResults(res);
                setIsLoading(false);
            });
        } else if (productId && showAnalyticsModal && props.isInPartnerContext) {
            setIsLoading(true);
            const data = campaignAnalyticsDataForPartner();
            data.then(res => {
                setCampaignResultsForPartner(res);
                setIsLoading(false);
            });
        }
    }, [productId, props.isInPartnerContext, showAnalyticsModal]);

    function showSettingsModal() {
        setSettingsVisible(true);
    }

    function hidePromotionModal() {
        setPromotionVisible(false);
    }

    function hideSettingModal() {
        setSettingsVisible(false);
    }

    function onAnalyticsClicked() {
        if (!showAnalyticsModal) {
            setShowAnalyticsModal(true);
            setProductId(campaignProductId);
        } else {
            setShowAnalyticsModal(false);
        }
    }

    function onDownloadPdfClick() {
        return downloadAnalyticsPdf(campaignProductId);
    }

    function onDownloadClick() {
        return downloadMarketingMaterial(campaignProductId);
    }

    function onPromotionClick() {
        setProductId(campaignProductId);
        setPromotionVisible(true);
    }

    const productAuthors = props.product.authors ?? [];
    const authorFullNames = productAuthors.map(author => Utils.getPerformerFullname(author));
    const authors = authorFullNames.join(', ');
    const campaignStartDate = product.campaignStart;
    const campaignEndDate = product.campaignEnd;
    const campaignTerm = product.termLimit;
    const diagramData = campaignResults || campaignResultsForPartner;

    function getListItemState(
        startDateInMS,
        loansLeft,
        endDateInMS,
        todayInMS,
        maxEndDateInMS,
        autoExtensionEndDateInMS,
        id
    ) {
        const isStartDateSet = startDateInMS;
        const isEndDateNotReached = endDateInMS > todayInMS;
        const isMaxEndDateNotReached = maxEndDateInMS > todayInMS;
        const isAutoExtendDateNotReached = autoExtensionEndDateInMS > todayInMS;
        const isAllEndDatesInFuture = isEndDateNotReached && isMaxEndDateNotReached && isAutoExtendDateNotReached;
        const isStartDateInFuture = startDateInMS > todayInMS;
        const isEndDateReached = isMaxEndDateNotReached && isAutoExtendDateNotReached;
        const isStartDateNullAndLoansLeftNull = !startDateInMS && loansLeft === 0;

        if (isStartDateSet) {
            if (loansLeft !== 0) {
                if (isAllEndDatesInFuture) {
                    if (isStartDateInFuture) {
                        return startDateInFuture;
                    } else {
                        return startDateReached;
                    }
                } else if (isEndDateReached) {
                    return endDateReached;
                } else if (isAutoExtendDateNotReached) {
                    return autoExtendDateNotReached;
                } else {
                    return allEndDatesReached;
                }
            } else {
                return startDateSetAndNoLoansLeft;
            }
        } else if (isStartDateNullAndLoansLeftNull) {
            log.error('start date was not set and loans left is 0', { id });
            return noStartDateAndNoLoansLeft;
        } else {
            return noStartDateSet;
        }
    }

    const {
        minStartDate,
        campaignStart,
        campaignEnd,
        campaignTagLine,
        campaignCopies,
        loansLeft,
        totalLoans,
        maxStartDate,
        releaseDate,
        campaignUsageCondition: { lendingModel, usageLimit, termLimit }
    } = product;

    const simultaneousAccess = lendingModel === LENDING_MODEL_SIM;
    const startDateInMS = Date.parse(campaignStart ?? '');
    const endDateInMS = Date.parse(campaignEnd ?? '');
    const maxStartDateInMs = Date.parse(maxStartDate ?? '');
    const minStartDateInMs = Date.parse(minStartDate ?? '');
    const MS_TO_HOURS = 3600 * 1000;
    const MS_TO_DAY = 24 * MS_TO_HOURS;
    const maxEndDateInMS = minStartDateInMs + termLimit * MS_TO_DAY;
    const autoExtensionEndDateInMS = maxEndDateInMS + 31 * MS_TO_DAY;
    const todayInMS = new Date().getTime();
    const daysLeftInMS = endDateInMS - todayInMS;
    const daysLeft = daysLeftInCampaignFromNow(new Date(campaignEnd ?? ''));
    const daysLeftInHours = Math.floor(daysLeftInMS / MS_TO_HOURS);

    const {
        showStartDateEditButton,
        showEndDateEditButton,
        showSubText,
        showDates,
        isStartDateInFuture,
        textGrayingStartDate,
        textGrayingEndDate,
        textGrayingExpiry,
        loansAndCopiesTextGraying,
        grayingProductListItem,
        showActionButtons,
        allowStartDateEditing,
        allowExpiryEditing
    } = getListItemState(
        startDateInMS,
        loansLeft,
        endDateInMS,
        todayInMS,
        maxEndDateInMS,
        autoExtensionEndDateInMS,
        campaignProductId
    );

    const loanFormatKey = props.loanFormat;
    const productLoanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
    const productLink = `/products/${campaignProductId}`;
    const titleAndSubTitle = product.title + (product.subTitle ? ` ${product.subTitle}` : '');
    const StMetaDataContainer = grayingProductListItem ? StTextGraying : 'div';

    const loanFormatName = loanFormatKeyToObject(product.loanFormat);
    const colorStartDate = textGrayingStartDate ? themeColor(THEME_GREY) : themeColor(THEME_BLACK);
    const colorEndDate = textGrayingEndDate ? themeColor(THEME_GREY) : themeColor(THEME_BLACK);
    const colorExpiry = textGrayingExpiry ? themeColor(THEME_GREY) : themeColor(THEME_BLACK);
    const iconMaterial = MATERIAL_ICON_EDIT;
    const badgeGraying = grayingProductListItem ? <StBadgeGraying /> : null;
    const isPreOrder = MetadataUtils.isProductPreOrderable(product);
    const colorActionsText = loansAndCopiesTextGraying ? themeColor(THEME_GREY) : themeColor(THEME_BLACK);

    return (
        <div className="product-list-row" style={{ position: 'relative' }}>
            <AnalyticsModal
                visible={showAnalyticsModal}
                title={props.product.title ?? ''}
                onClose={onAnalyticsClicked}
                campaignStartDate={campaignStartDate ?? ''}
                campaignEndDate={campaignEndDate ?? ''}
                campaignCopies={campaignCopies ?? 0}
                onDownloadAnalyticsPdf={onDownloadPdfClick}
                diagramData={diagramData ?? ({} as CampaignResult)}
                loansLeft={loansLeft ?? 0}
                totalLoans={totalLoans ?? 0}
                campaignTerm={campaignTerm ?? 0}
                daysLeft={daysLeft}
                authors={authors}
                isInPartnerContext={props.isInPartnerContext}
            />
            <div
                className={classNames('product-list-cell', 'packshot', 'catalogue', {
                    over: showDetails
                })}
            >
                {grayingProductListItem ? <StCoverGraying /> : null}
                <Link
                    to={productLink}
                    onClick={e => {
                        e.preventDefault();
                        props.onProductImageClick();
                    }}
                >
                    <ProductCover
                        onMouseEnter={() => setShowDetails(true)}
                        onMouseLeave={() => setShowDetails(false)}
                        minSizeInPx={288}
                        title={product.title}
                        coverUrl={product.coverUrl}
                    />
                </Link>
                <PreviewButton product={product} />
            </div>
            <SettingsModal
                visible={settingsVisible}
                onClose={hideSettingModal}
                salesStartDate={product.releaseDate}
                campaignStartDate={campaignStart}
                campaignEndDate={campaignEnd}
                campaignTagLine={campaignTagLine}
                allowStartDateEditing={allowStartDateEditing}
                allowExpiryEditing={allowExpiryEditing}
                minStartDateInMs={minStartDateInMs}
                maxStartDateInMs={maxStartDateInMs}
                termLimit={termLimit}
                updateProductCampaignStart={props.updateProductCampaignSettings}
                productId={campaignProductId}
            />
            <PromotionModal
                campaignProductId={campaignProductId}
                promotionData={promotionData ?? ({} as PromotionLinkResponse)}
                visible={promotionVisible && !isPromotionLoading}
                onClose={hidePromotionModal}
                onError={message => {
                    dispatch(showNotification(message, TYPE_WARNING));
                }}
            />
            <div className="product-list-cell">
                <div className="product-list-details">
                    <div style={{ display: 'table-row' }}>
                        <StMetaDataContainer className="tc tcw-25" data-testid="metadata-container">
                            <ProductSeries {...props} loanFormatKey={loanFormatKey} />
                            <p title={titleAndSubTitle} className="label">
                                <Link to={productLink}>
                                    <strong>{Utils.truncate(titleAndSubTitle)}</strong>
                                </Link>
                            </p>
                            <ProductAuthors {...props} loanFormatKey={loanFormatKey} />
                            <ProductNarrators {...props} loanFormatKey={productLoanFormatKey} />
                        </StMetaDataContainer>
                        <StMetaDataContainer className="tc tcw-25">
                            <ProductFormat {...props} loanFormatKey={productLoanFormatKey} />
                            <ProductGenres {...props} loanFormatKey={loanFormatKey} />
                            <ProductAge {...props} loanFormatKey={loanFormatKey} />
                            <ProductLanguage product={product} loanFormatKey={loanFormatKey} label={product.language} />
                        </StMetaDataContainer>
                        <StMetaDataContainer className="tc tcw-25">
                            <p>{product.isbn13}</p>
                            <ProductReleaseDate {...props} />
                            <ProductPublisher {...props} loanFormatKey={loanFormatKey} />
                        </StMetaDataContainer>
                        <div className="tc tcw-10" />
                        <StActions>
                            <StProductListCell>
                                <StCampaignCopiesWrapper color={colorActionsText}>
                                    <Copies campaignCopies={campaignCopies ?? 0} />
                                </StCampaignCopiesWrapper>
                                <StCampaignCopiesWrapper color={colorActionsText}>
                                    <TotalLoans
                                        loansLeft={loansLeft ?? 0}
                                        totalLoans={totalLoans ?? 0}
                                        loansAndCopiesTextGraying={loansAndCopiesTextGraying}
                                    />
                                </StCampaignCopiesWrapper>
                            </StProductListCell>
                            {showActionButtons ? (
                                <StActionButtons>
                                    <AddProduct
                                        key="addProduct"
                                        product={props.product}
                                        onAddPrice={onAddPrice}
                                        onSelectPrice={async () => {}}
                                        showSelectedPriceOnly={false}
                                        allowSelection={true}
                                    />
                                </StActionButtons>
                            ) : (
                                <StProductUnavailable>
                                    {t('marketing.productGroups.warnings.productUnavailable')}
                                </StProductUnavailable>
                            )}
                        </StActions>
                    </div>
                </div>
                <StCellBottom>
                    <div className="tc tcw-25">
                        <StBadges>
                            {badgeGraying}
                            <CampaignUsageBadge
                                loanFormat={loanFormatName?.singularName}
                                usageLimit={usageLimit}
                                campaignTerm={termLimit}
                                isSimultaneousAccess={simultaneousAccess}
                                releaseDate={releaseDate}
                                isPreOrder={isPreOrder}
                            />
                        </StBadges>
                    </div>
                    {showDates ? (
                        <StCampaignDates className="tc tcw-50">
                            <StCampaignDatesWrapper color={colorStartDate}>
                                <StartDate
                                    campaignStartDate={campaignStart ?? ''}
                                    icon={iconMaterial}
                                    showStartDateEditButton={showStartDateEditButton}
                                    onEditButtonClick={showSettingsModal}
                                />
                            </StCampaignDatesWrapper>
                            <StCampaignDatesWrapper color={colorEndDate}>
                                <EndDate
                                    campaignEndDate={campaignEnd ?? ''}
                                    daysLeft={daysLeft}
                                    icon={iconMaterial}
                                    showEndDateEditButton={showEndDateEditButton}
                                    showSubText={showSubText}
                                    onEditButtonClick={showSettingsModal}
                                    loansLeft={loansLeft}
                                    daysLeftInHours={daysLeftInHours}
                                />
                            </StCampaignDatesWrapper>
                            <StCampaignDatesWrapper color={colorExpiry}>
                                <TagLine
                                    icon={iconMaterial}
                                    editAllowed={showEndDateEditButton}
                                    onEditButtonClick={showSettingsModal}
                                    tagLineContent={campaignTagLine ?? ''}
                                    grayingProductListItem={grayingProductListItem}
                                />
                            </StCampaignDatesWrapper>
                        </StCampaignDates>
                    ) : null}
                    <div className="tc tcw-10"></div>
                    {showDates ? (
                        <>
                            {isStartDateInFuture ? (
                                <StButtonPos>
                                    <StButtonV2>
                                        <Button
                                            text={t('marketing.yourCampaigns.viewPromotionLinkAndQr')}
                                            icon={MATERIAL_ICON_LINK}
                                            isLoading={isPromotionLoading}
                                            size={ButtonSize.LARGE}
                                            theme={ButtonTheme.NEUTRAL}
                                            fullWidth={true}
                                            onClick={onPromotionClick}
                                        />
                                    </StButtonV2>
                                    <StButtonV2>
                                        <Button
                                            text={t('marketing.yourCampaigns.downloadMarketingMaterial')}
                                            icon="file_download"
                                            size={ButtonSize.LARGE}
                                            theme={ButtonTheme.NEUTRAL}
                                            fullWidth={true}
                                            onClick={onDownloadClick}
                                        />
                                    </StButtonV2>
                                </StButtonPos>
                            ) : null}
                            {autoExtensionEndDateInMS <= todayInMS || loansLeft === 0 ? (
                                <StButtonPos>
                                    <StButtonV2>
                                        <Button
                                            isLoading={isLoading}
                                            text={t('marketing.yourCampaigns.viewAnalytics')}
                                            icon="assessment"
                                            size={ButtonSize.LARGE}
                                            fullWidth={true}
                                            onClick={onAnalyticsClicked}
                                            disabled={startDateInMS > todayInMS}
                                        />
                                    </StButtonV2>
                                    <StButtonV2>
                                        <Button
                                            text={t('marketing.yourCampaigns.viewPromotionLinkAndQr')}
                                            icon={MATERIAL_ICON_LINK}
                                            isLoading={isPromotionLoading}
                                            size={ButtonSize.LARGE}
                                            theme={ButtonTheme.NEUTRAL}
                                            fullWidth={true}
                                            onClick={onPromotionClick}
                                        />
                                    </StButtonV2>
                                    <StButtonV2>
                                        <Button
                                            text={t('marketing.yourCampaigns.downloadMarketingMaterial')}
                                            icon="file_download"
                                            size={ButtonSize.LARGE}
                                            theme={ButtonTheme.NEUTRAL}
                                            fullWidth={true}
                                            onClick={onDownloadClick}
                                        />
                                    </StButtonV2>
                                </StButtonPos>
                            ) : (
                                <StButtonPos>
                                    <StButtonV2>
                                        <Button
                                            text={t('marketing.yourCampaigns.viewAnalytics')}
                                            isLoading={isLoading}
                                            icon="assessment"
                                            size={ButtonSize.LARGE}
                                            fullWidth={true}
                                            onClick={onAnalyticsClicked}
                                            disabled={startDateInMS > todayInMS}
                                        />
                                    </StButtonV2>
                                    <StButtonV2>
                                        <Button
                                            text={t('marketing.yourCampaigns.viewPromotionLinkAndQr')}
                                            isLoading={isPromotionLoading}
                                            icon={MATERIAL_ICON_LINK}
                                            size={ButtonSize.LARGE}
                                            theme={ButtonTheme.NEUTRAL}
                                            fullWidth={true}
                                            onClick={onPromotionClick}
                                        />
                                    </StButtonV2>
                                    <StButtonV2>
                                        <Button
                                            text={t('marketing.yourCampaigns.downloadMarketingMaterial')}
                                            icon="file_download"
                                            size={ButtonSize.LARGE}
                                            theme={ButtonTheme.NEUTRAL}
                                            fullWidth={true}
                                            onClick={onDownloadClick}
                                        />
                                    </StButtonV2>
                                </StButtonPos>
                            )}
                        </>
                    ) : !showDates && loansLeft === 0 ? (
                        <StButtonV2>
                            <Button
                                text={t('marketing.yourCampaigns.viewAnalytics')}
                                isLoading={isLoading}
                                icon="assessment"
                                size={ButtonSize.LARGE}
                                fullWidth={true}
                                onClick={onAnalyticsClicked}
                                disabled={startDateInMS > todayInMS}
                            />
                        </StButtonV2>
                    ) : (
                        <StButtonPos>
                            <StButtonV2>
                                <Button
                                    text={t('marketing.yourCampaigns.setCampaignStart')}
                                    icon="event"
                                    size={ButtonSize.LARGE}
                                    fullWidth={true}
                                    onClick={showSettingsModal}
                                />
                            </StButtonV2>
                            <StButtonV2>
                                <Button
                                    text={t('marketing.yourCampaigns.viewPromotionLinkAndQr')}
                                    icon={MATERIAL_ICON_LINK}
                                    size={ButtonSize.LARGE}
                                    theme={ButtonTheme.NEUTRAL}
                                    fullWidth={true}
                                    onClick={onPromotionClick}
                                    isLoading={isPromotionLoading}
                                />
                            </StButtonV2>
                            <StButtonV2>
                                <Button
                                    text={t('marketing.yourCampaigns.downloadMarketingMaterial')}
                                    icon="file_download"
                                    size={ButtonSize.LARGE}
                                    theme={ButtonTheme.NEUTRAL}
                                    fullWidth={true}
                                    onClick={onDownloadClick}
                                />
                            </StButtonV2>
                        </StButtonPos>
                    )}
                </StCellBottom>
            </div>
        </div>
    );
};
